import React from 'react';
import { motion } from 'framer-motion';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { EVENT_TYPES, EVENT_CATEGORIES } from '../data/events';
export interface EventFilterState {
  search: string;
  type: string;
  category: string;
  startDate: string;
  endDate: string;
  sort: 'date-asc' | 'date-desc' | 'title-asc';
}
interface EventFiltersProps {
  filters: EventFilterState;
  onChange: (next: Partial<EventFilterState>) => void;
  onReset: () => void;
  resultCount: number;
  totalCount: number;
}
const selectClasses = 'w-full appearance-none bg-[#0B1014] border border-white/10 text-white text-sm px-4 py-3 focus:outline-none focus:border-[#DDA247] transition-colors [color-scheme:dark] rounded';
export function EventFilters({
  filters,
  onChange,
  onReset,
  resultCount,
  totalCount
}: EventFiltersProps) {
  const hasActiveFilters = filters.search !== '' || filters.type !== 'all' || filters.category !== 'all' || filters.startDate !== '' || filters.endDate !== '';
  return <motion.div initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.5
  }} className="bg-[#11161A] border border-white/10 rounded-2xl p-5 md:p-6 mb-10" data-id="element-246">
      <div className="flex items-center justify-between mb-5" data-id="element-247">
        <div className="flex items-center gap-2 text-white" data-id="element-248">
          <SlidersHorizontal className="w-4 h-4 text-[#DDA247]" data-id="element-249" />
          <h2 className="text-sm font-bold uppercase tracking-widest" data-id="element-250">
            Filter Events
          </h2>
        </div>
        {hasActiveFilters && <button onClick={onReset} className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-[#DDA247] transition-colors uppercase tracking-wider" data-id="element-251">
            <X className="w-3.5 h-3.5" data-id="element-252" />
            Clear
          </button>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4" data-id="element-253">
        {/* Search */}
        <div className="lg:col-span-4" data-id="element-254">
          <label className="block text-[10px] text-gray-400 font-medium tracking-widest uppercase mb-2" data-id="element-255">
            Search
          </label>
          <div className="relative" data-id="element-256">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" data-id="element-257" />
            <input type="text" value={filters.search} onChange={e => onChange({
            search: e.target.value
          })} placeholder="Search by title, artist, venue…" className="w-full bg-[#0B1014] border border-white/10 text-white text-sm pl-9 pr-4 py-3 focus:outline-none focus:border-[#DDA247] transition-colors rounded placeholder:text-gray-600" data-id="element-258" />
          </div>
        </div>

        {/* Event Type */}
        <div className="lg:col-span-2" data-id="element-259">
          <label className="block text-[10px] text-gray-400 font-medium tracking-widest uppercase mb-2" data-id="element-260">
            Event Type
          </label>
          <select value={filters.type} onChange={e => onChange({
          type: e.target.value
        })} className={selectClasses} data-id="element-261">
            <option value="all" data-id="element-262">All Days</option>
            {EVENT_TYPES.map(t => <option key={t} value={t} data-id="element-263">
                {t.charAt(0) + t.slice(1).toLowerCase()}
              </option>)}
          </select>
        </div>

        {/* Category */}
        <div className="lg:col-span-2" data-id="element-264">
          <label className="block text-[10px] text-gray-400 font-medium tracking-widest uppercase mb-2" data-id="element-265">
            Category
          </label>
          <select value={filters.category} onChange={e => onChange({
          category: e.target.value
        })} className={selectClasses} data-id="element-266">
            <option value="all" data-id="element-267">All Categories</option>
            {EVENT_CATEGORIES.map(c => <option key={c} value={c} data-id="element-268">
                {c}
              </option>)}
          </select>
        </div>

        {/* Sort */}
        <div className="lg:col-span-4" data-id="element-269">
          <label className="block text-[10px] text-gray-400 font-medium tracking-widest uppercase mb-2" data-id="element-270">
            Sort By
          </label>
          <select value={filters.sort} onChange={e => onChange({
          sort: e.target.value as EventFilterState['sort']
        })} className={selectClasses} data-id="element-271">
            <option value="date-asc" data-id="element-272">Date — Soonest First</option>
            <option value="date-desc" data-id="element-273">Date — Latest First</option>
            <option value="title-asc" data-id="element-274">Title — A to Z</option>
          </select>
        </div>

        {/* Start Date */}
        <div className="lg:col-span-6" data-id="element-275">
          <label className="block text-[10px] text-gray-400 font-medium tracking-widest uppercase mb-2" data-id="element-276">
            From Date
          </label>
          <input type="date" value={filters.startDate} onChange={e => onChange({
          startDate: e.target.value
        })} className={selectClasses} data-id="element-277" />
        </div>

        {/* End Date */}
        <div className="lg:col-span-6" data-id="element-278">
          <label className="block text-[10px] text-gray-400 font-medium tracking-widest uppercase mb-2" data-id="element-279">
            To Date
          </label>
          <input type="date" value={filters.endDate} onChange={e => onChange({
          endDate: e.target.value
        })} className={selectClasses} data-id="element-280" />
        </div>
      </div>

      <p className="mt-5 text-xs text-gray-500 tracking-wide" data-id="element-281">
        Showing <span className="text-[#DDA247] font-bold" data-id="element-282">{resultCount}</span>{' '}
        of {totalCount} events
      </p>
    </motion.div>;
}